import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckboxField,
  FormControl,
  NumberField,
  RadioGroup,
  SearchField,
  SegmentedControl,
  SelectField,
  TextareaField,
  TextField,
} from "../components/common/Form";
import {
  Calendar,
  DatePicker,
  MonthPicker,
  MultiDatePicker,
  QuarterRangePicker,
  YearPicker,
} from "../components/common/Date";
import type {
  CalendarDate,
  CalendarDateRange,
  DateControlLocale,
  YearMonth,
  YearQuarter,
} from "../components/common/Date";
import { Card } from "../components/common/Card";
import { Section } from "../components/common/Section";
import type { DashboardDictionary } from "../i18n/dictionary";
import { PageIntro } from "./PageIntro";

interface ComponentLabPageProps {
  copy: DashboardDictionary;
}

const TEXT_COUNTER_LIMIT = 200;
const TEXTAREA_COUNTER_LIMIT = 200;

const createTodayValue = (): CalendarDate => {
  const today = new Date();

  return {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: today.getDate(),
  };
};

const createTodayMonth = (): YearMonth => {
  const today = createTodayValue();

  return {
    year: today.year,
    month: today.month,
  };
};

const createTodayQuarter = (): YearQuarter => {
  const today = createTodayValue();

  return {
    year: today.year,
    quarter: Math.ceil(today.month / 3),
  };
};

const createTodayRange = (): CalendarDateRange => {
  const today = createTodayValue();

  return {
    start: today,
    end: today,
  };
};

export const ComponentLabPage = ({ copy }: ComponentLabPageProps) => {
  const lab = copy.componentLab;
  const isKorean = copy.dateTimeLocale === "ko-KR";
  const dateLocale: DateControlLocale = isKorean ? "ko" : "en";
  const [keyword, setKeyword] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [useTextValue, setUseTextValue] = useState("");
  const [description, setDescription] = useState("");
  const [score, setScore] = useState(80);
  const [status, setStatus] = useState(lab.selectOptions[0]?.value ?? "");
  const [isConfirmed, setIsConfirmed] = useState(true);
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);
  const [notificationChannel, setNotificationChannel] = useState(
    lab.radioOptions[0]?.value ?? "",
  );
  const [activeTab, setActiveTab] = useState("overview");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarDuration, setSnackbarDuration] = useState(3);
  const [snackbarType, setSnackbarType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [snackbarKey, setSnackbarKey] = useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [monthValue, setMonthValue] = useState<YearMonth>(createTodayMonth);
  const [quarterValue, setQuarterValue] =
    useState<YearQuarter>(createTodayQuarter);
  const [yearValue, setYearValue] = useState(() => createTodayValue().year);
  const [calendarValue, setCalendarValue] =
    useState<CalendarDate>(createTodayValue);
  const [actionCalendarValue, setActionCalendarValue] =
    useState<CalendarDate>(createTodayValue);
  const [datePickerValue, setDatePickerValue] =
    useState<CalendarDate>(createTodayValue);
  const [calendarRange, setCalendarRange] =
    useState<CalendarDateRange>(createTodayRange);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const textSamples = [
    ["xs", "12px"],
    ["sm", "14px"],
    ["md", "16px"],
    ["lg", "20px"],
    ["xl", "28px"],
  ];
  const colorSamples = [
    ["default", isKorean ? "기본 텍스트" : "Default text"],
    ["muted", isKorean ? "보조 텍스트" : "Muted text"],
    ["blue", isKorean ? "파란 텍스트" : "Blue text"],
    ["green", isKorean ? "초록 텍스트" : "Green text"],
    ["rose", isKorean ? "위험 텍스트" : "Danger text"],
  ];
  const tabs = [
    { id: "overview", label: isKorean ? "개요" : "Overview" },
    { id: "state", label: isKorean ? "상태" : "States" },
    { id: "a11y", label: isKorean ? "접근성" : "A11y" },
  ];
  const roundedTabOptions = tabs.map((tab) => ({
    value: tab.id,
    label: tab.label,
  }));
  const switchSizeOptions = [
    { size: "xs", color: "var(--blue)" },
    { size: "sm", color: "var(--blue)" },
    { size: "md", color: "var(--blue)" },
    { size: "default", color: "var(--blue)" },
    { size: "lg", color: "var(--blue)" },
    { size: "xl", color: "var(--blue)" },
  ];
  const switchToneOptions = [
    { label: "Default", color: "var(--blue)" },
    { label: "Green", color: "var(--green)" },
    { label: "Rose", color: "var(--rose)" },
  ];
  const textareaDisabledValue = isKorean ? "내용을 입력해주세요." : "Enter content.";
  const useTextCounter = `${useTextValue.length}/${TEXT_COUNTER_LIMIT}`;
  const textareaCounter = `${description.length}/${TEXTAREA_COUNTER_LIMIT}`;
  const disabledTextareaCounter = `${textareaDisabledValue.length}/${TEXTAREA_COUNTER_LIMIT}`;
  const snackbarCopy = {
    success: isKorean ? "완료되었습니다." : "Completed.",
    error: isKorean ? "에러가 발생했습니다." : "An error occurred.",
    info: isKorean
      ? "중요 정보를 알릴때 사용합니다."
      : "Use this for important information.",
    warning: isKorean ? "경고 메시지입니다." : "Warning message.",
  };
  const switchSampleLabel = isAutoSaveEnabled
    ? isKorean
      ? "ON 스위치"
      : "On switch"
    : isKorean
      ? "OFF 스위치"
      : "Off switch";
  const snackbarIcon = {
    success: "✓",
    error: "!",
    info: "i",
    warning: "▲",
  };
  const showSnackbar = (type: typeof snackbarType) => {
    setSnackbarType(type);
    setSnackbarKey((current) => current + 1);
    setIsSnackbarOpen(true);
  };
  const renderSearchIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </svg>
  );
  const iconButtons = [
    {
      label: isKorean ? "검색" : "Search",
      paths: [
        <circle cx="11" cy="11" r="6" key="circle" />,
        <path d="m16 16 4 4" key="line" />,
      ],
    },
    {
      label: isKorean ? "추가" : "Add",
      paths: [<path d="M12 5v14" key="v" />, <path d="M5 12h14" key="h" />],
    },
    {
      label: isKorean ? "삭제" : "Remove",
      paths: [<path d="M5 12h14" key="h" />],
    },
    {
      label: isKorean ? "닫기" : "Close",
      paths: [
        <path d="m6 6 12 12" key="a" />,
        <path d="m18 6-12 12" key="b" />,
      ],
    },
    {
      label: isKorean ? "새로고침" : "Refresh",
      paths: [
        <path d="M20 11a8.1 8.1 0 0 0-14.9-4.2L3 9" key="a" />,
        <path d="M3 4v5h5" key="b" />,
        <path d="M4 13a8.1 8.1 0 0 0 14.9 4.2L21 15" key="c" />,
        <path d="M16 15h5v5" key="d" />,
      ],
    },
  ];

  useEffect(() => {
    if (!isPopoverOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        popoverRef.current &&
        event.target instanceof Node &&
        !popoverRef.current.contains(event.target)
      ) {
        setIsPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!isSnackbarOpen) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => {
        setIsSnackbarOpen(false);
      },
      Math.max(snackbarDuration, 1) * 1000,
    );

    return () => window.clearTimeout(timer);
  }, [isSnackbarOpen, snackbarDuration, snackbarKey]);

  const snackbarRoot =
    typeof document === "undefined"
      ? null
      : document.getElementById("snackbar") ?? document.body;

  return (
    <>
      <PageIntro id="component-lab-page" copy={copy.pages.componentLab} />
      <Section
        id="component-lab"
        eyebrow={copy.sections.componentLab.eyebrow}
        title={copy.sections.componentLab.title}
        description={copy.sections.componentLab.description}
      >
        <Card
          className="component-lab__positioning"
          title={lab.positioningTitle}
          description={lab.positioningDescription}
        >
          <ul className="component-lab__positioning-list">
            {lab.positioningItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Card>

        <div className="component-lab__catalog">
          <Card
            className="component-lab__catalog-card component-lab__catalog-card--full"
            title={isKorean ? "Text" : "Text"}
            description={
              isKorean
                ? "크기와 색상별 텍스트 토큰입니다."
                : "Text tokens by size and color."
            }
          >
            <div className="component-lab__demo-stack">
              <div className="component-lab__demo-row">
                {textSamples.map(([size, label]) => (
                  <span className={`lab-text lab-text--${size}`} key={size}>
                    {label}
                  </span>
                ))}
              </div>
              <div className="component-lab__demo-row">
                {colorSamples.map(([color, label]) => (
                  <span
                    className={`lab-text-color lab-text-color--${color}`}
                    key={color}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card component-lab__catalog-card--wide"
            title={isKorean ? "Button" : "Button"}
            description={
              isKorean
                ? "사이즈, radius, 색상, 옅은 배경, 진한 배경, 아이콘 버튼 상태입니다."
                : "Button sizes, radii, colors, soft backgrounds, dark backgrounds, and icon buttons."
            }
          >
            <div className="component-lab__demo-stack">
              <div className="component-lab__subsection">
                <strong>{isKorean ? "사이즈별" : "Sizes"}</strong>
              </div>
              <div className="component-lab__demo-row">
                <button type="button" className="lab-button lab-button--sm">
                  sm
                </button>
                <button type="button" className="lab-button">
                  md
                </button>
                <button type="button" className="lab-button lab-button--lg">
                  lg
                </button>
              </div>
              <div className="component-lab__subsection">
                <strong>{isKorean ? "둥근 정도" : "Radius"}</strong>
              </div>
              <div className="component-lab__demo-row">
                <button type="button" className="lab-button lab-button--square">
                  4px
                </button>
                <button
                  type="button"
                  className="lab-button lab-button--rounded"
                >
                  8px
                </button>
                <button type="button" className="lab-button lab-button--pill">
                  999px
                </button>
              </div>
              <div className="component-lab__subsection">
                <strong>
                  {isKorean ? "색상과 배경" : "Colors and backgrounds"}
                </strong>
              </div>
              <div className="component-lab__demo-row">
                <button type="button" className="lab-button lab-button--blue">
                  Blue
                </button>
                <button type="button" className="lab-button lab-button--green">
                  Green
                </button>
                <button type="button" className="lab-button lab-button--rose">
                  Rose
                </button>
                <button type="button" className="lab-button lab-button--soft">
                  Soft
                </button>
                <button type="button" className="lab-button lab-button--dark">
                  Dark
                </button>
              </div>
              <div className="component-lab__subsection">
                <strong>{isKorean ? "아이콘 버튼" : "Icon buttons"}</strong>
              </div>
              <div className="component-lab__demo-row">
                {iconButtons.map((item) => (
                  <button
                    type="button"
                    className="lab-icon-button"
                    aria-label={item.label}
                    key={item.label}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      {item.paths}
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card component-lab__catalog-card--full"
            title={lab.fieldCard.title}
            description={lab.fieldCard.description}
          >
            <div className="component-lab__input-catalog">
              <div className="component-lab__field-surface">
                <div className="component-lab__subsection">
                  <strong>
                    {isKorean ? "공통 입력 필드" : "Common fields"}
                  </strong>
                </div>
                <FormControl className="component-lab__form-stack">
                  <SearchField
                    label={lab.fields.keywordLabel}
                    value={keyword}
                    placeholder={lab.fields.keywordPlaceholder}
                    helperText={lab.fields.keywordHelper}
                    onChange={(event) => setKeyword(event.target.value)}
                  />
                  <TextField
                    label={lab.fields.textLabel}
                    value={fieldName}
                    placeholder={lab.fields.textPlaceholder}
                    helperText={lab.fields.textHelper}
                    onChange={(event) => setFieldName(event.target.value)}
                  />
                  <NumberField
                    label={lab.fields.numberLabel}
                    value={score}
                    min="0"
                    max="100"
                    helperText={lab.fields.numberHelper}
                    onChange={(event) => setScore(Number(event.target.value))}
                  />
                  <SelectField
                    label={lab.fields.selectLabel}
                    value={status}
                    options={lab.selectOptions}
                    helperText={lab.fields.selectHelper}
                    onValueChange={setStatus}
                  />

                  <TextField
                    label={lab.fields.disabledLabel}
                    value={lab.fields.disabledValue}
                    helperText={lab.fields.disabledHelper}
                    disabled
                    readOnly
                  />
                  <TextField
                    label={lab.fields.requiredLabel}
                    value=""
                    placeholder={lab.fields.requiredPlaceholder}
                    error={lab.fields.requiredError}
                    required
                    readOnly
                  />
                </FormControl>
              </div>

              <div className="input-spec">
                <div className="component-lab__subsection">
                  <strong>Input</strong>
                </div>
                <div className="input-spec__group">
                  <strong>Default : height(28px)</strong>
                  <div className="input-spec__row">
                    <span className="lab-input">
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                    </span>
                    <span className="lab-input is-on">
                      <input value="class= on" readOnly />
                    </span>
                    <span className="lab-input is-focused">
                      <input value="class=focused" readOnly />
                    </span>
                    <span className="lab-input lab-input--wide">
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                    </span>
                  </div>
                </div>
                <div className="input-spec__group">
                  <strong>Disabled</strong>
                  <div className="input-spec__row">
                    <span className="lab-input is-disabled">
                      <input value="class='disabled'" disabled />
                    </span>
                    <span className="lab-input lab-input--with-button is-disabled">
                      <input value="class='disabled'" disabled />
                      <button
                        type="button"
                        aria-label={isKorean ? "지우기" : "Clear"}
                        disabled
                      >
                        ×
                      </button>
                      <button
                        type="button"
                        aria-label={isKorean ? "검색" : "Search"}
                        disabled
                      >
                        {renderSearchIcon()}
                      </button>
                    </span>
                  </div>
                </div>
                <div className="input-spec__group">
                  <strong>Use Button</strong>
                  <div className="input-spec__row">
                    <span className="lab-input lab-input--with-button">
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                      <button
                        type="button"
                        aria-label={isKorean ? "검색" : "Search"}
                      >
                        {renderSearchIcon()}
                      </button>
                    </span>
                    <span className="lab-input lab-input--with-button">
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                      <button
                        type="button"
                        aria-label={isKorean ? "지우기" : "Clear"}
                      >
                        ×
                      </button>
                      <button
                        type="button"
                        aria-label={isKorean ? "검색" : "Search"}
                      >
                        {renderSearchIcon()}
                      </button>
                    </span>
                  </div>
                </div>
                <div className="input-spec__group">
                  <strong>Use Button : RTL</strong>
                  <div className="input-spec__row">
                    <span className="lab-input lab-input--with-button lab-input--rtl">
                      <button
                        type="button"
                        aria-label={isKorean ? "검색" : "Search"}
                      >
                        {renderSearchIcon()}
                      </button>
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                    </span>
                    <span className="lab-input lab-input--with-button lab-input--rtl">
                      <button
                        type="button"
                        aria-label={isKorean ? "검색" : "Search"}
                      >
                        {renderSearchIcon()}
                      </button>
                      <button
                        type="button"
                        aria-label={isKorean ? "지우기" : "Clear"}
                      >
                        ×
                      </button>
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                    </span>
                  </div>
                </div>
                <div className="input-spec__group">
                  <strong>Use Text</strong>
                  <span className="lab-input lab-input--with-text">
                    <input
                      value={useTextValue}
                      maxLength={TEXT_COUNTER_LIMIT}
                      placeholder={
                        isKorean ? "OOO을 입력해 주세요" : "Enter value"
                      }
                      onChange={(event) =>
                        setUseTextValue(
                          event.target.value.slice(0, TEXT_COUNTER_LIMIT),
                        )
                      }
                    />
                    <span className="lab-counter lab-counter--sm">
                      {useTextCounter}
                    </span>
                  </span>
                </div>
                <div className="input-spec__group">
                  <strong>Size : sm(22px)</strong>
                  <div className="input-spec__row">
                    <span className="lab-input lab-input--sm">
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                    </span>
                    <span className="lab-input lab-input--sm lab-input--with-button">
                      <input
                        placeholder={
                          isKorean ? "OOO을 입력해 주세요" : "Enter value"
                        }
                      />
                      <button
                        type="button"
                        aria-label={isKorean ? "지우기" : "Clear"}
                      >
                        ×
                      </button>
                      <button
                        type="button"
                        aria-label={isKorean ? "검색" : "Search"}
                      >
                        {renderSearchIcon()}
                      </button>
                    </span>
                  </div>
                </div>
              </div>

              <div className="textarea-spec">
                <div className="component-lab__subsection">
                  <strong>Textarea</strong>
                </div>
                <TextareaField
                  label={lab.fields.textareaLabel}
                  value={description}
                  maxLength={TEXTAREA_COUNTER_LIMIT}
                  placeholder={lab.fields.textareaPlaceholder}
                  helperText={lab.fields.textareaHelper}
                  rows={4}
                  counterText={textareaCounter}
                  onChange={(event) =>
                    setDescription(
                      event.target.value.slice(0, TEXTAREA_COUNTER_LIMIT),
                    )
                  }
                />
                <TextareaField
                  label={isKorean ? "비활성 Textarea" : "Disabled"}
                  value={textareaDisabledValue}
                  rows={4}
                  counterText={disabledTextareaCounter}
                  disabled
                  readOnly
                />
              </div>
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card component-lab__catalog-card--wide"
            title={lab.choiceCard.title}
            description={lab.choiceCard.description}
          >
            <FormControl className="component-lab__form-stack">
              <div className="component-lab__control-row">
                <CheckboxField
                  label={lab.fields.checkboxLabel}
                  helperText={lab.fields.checkboxHelper}
                  checked={isConfirmed}
                  onChange={(event) => setIsConfirmed(event.target.checked)}
                />
                <CheckboxField
                  label={isKorean ? "비활성 체크박스" : "Disabled checkbox"}
                  checked
                  disabled
                  helperText={
                    isKorean ? "disabled 상태입니다." : "Disabled state."
                  }
                  readOnly
                />
              </div>
              <div className="component-lab__control-row">
                <label className="lab-round-check">
                  <input type="checkbox" defaultChecked />
                  <span aria-hidden="true" />
                  {isKorean ? "둥근 체크박스" : "Round checkbox"}
                </label>
              </div>
              <div className="component-lab__control-row">
                <RadioGroup
                  legend={lab.fields.radioLegend}
                  name="component-lab-notification"
                  value={notificationChannel}
                  options={lab.radioOptions}
                  helperText={lab.fields.radioHelper}
                  onChange={setNotificationChannel}
                />
              </div>
              <div className="component-lab__subsection">
                <strong>
                  {isKorean ? "Default : height 28px" : "Default : height 28px"}
                </strong>
              </div>
              <div className="lab-switch-showcase">
                <label
                  className={`lab-toggle-pill lab-toggle-pill--sample${isAutoSaveEnabled ? " is-on" : ""}`}
                  aria-label={switchSampleLabel}
                  style={
                    { "--switch-on-color": "var(--blue)" } as CSSProperties
                  }
                >
                  <input
                    type="checkbox"
                    checked={isAutoSaveEnabled}
                    onChange={(event) =>
                      setIsAutoSaveEnabled(event.target.checked)
                    }
                  />
                  <span aria-hidden="true" />
                  <em>{isAutoSaveEnabled ? "On" : "Off"}</em>
                </label>
                <label className="lab-toggle-pill is-disabled">
                  <input type="checkbox" disabled />
                  <span aria-hidden="true" />
                  <em>Disabled</em>
                </label>
              </div>
              <div className="component-lab__subsection">
                <strong>
                  {isKorean
                    ? "Size : xs(16px), sm(22px), md(24px), default(28px), lg(30px), xl(34px)"
                    : "Size : xs(16px), sm(22px), md(24px), default(28px), lg(30px), xl(34px)"}
                </strong>
              </div>
              <div
                className="lab-switch-size-row"
                aria-label={isKorean ? "사이즈별 온오프" : "Switch sizes"}
              >
                {switchSizeOptions.map((option) => (
                  <label
                    className={`lab-switch lab-switch--${option.size}`}
                    key={option.size}
                    style={
                      { "--switch-on-color": option.color } as CSSProperties
                    }
                  >
                    <input
                      type="checkbox"
                      aria-label={`${option.size} ${isKorean ? "스위치" : "switch"}`}
                      defaultChecked
                    />
                    <span aria-hidden="true" />
                  </label>
                ))}
              </div>
              <div className="component-lab__subsection">
                <strong>{isKorean ? "색상 옵션" : "Color options"}</strong>
              </div>
              <div
                className="lab-switch-size-row"
                aria-label={isKorean ? "색상별 온오프" : "Switch colors"}
              >
                {switchToneOptions.map((option) => (
                  <label
                    className="lab-switch lab-switch--default"
                    key={option.label}
                    style={
                      { "--switch-on-color": option.color } as CSSProperties
                    }
                  >
                    <input type="checkbox" defaultChecked />
                    <span aria-hidden="true" />
                    <em>{option.label}</em>
                  </label>
                ))}
              </div>
            </FormControl>
          </Card>

          <Card
            className="component-lab__catalog-card"
            title={isKorean ? "Label" : "Label"}
            description={
              isKorean
                ? "사이즈, 색상, 둥근 여부별 라벨입니다."
                : "Labels by size, color, and radius."
            }
          >
            <div className="component-lab__demo-stack">
              <div className="component-lab__subsection">
                <strong>{isKorean ? "사이즈별" : "Sizes"}</strong>
              </div>
              <div className="component-lab__demo-row">
                <span className="lab-label lab-label--sm">sm</span>
                <span className="lab-label">Default</span>
                <span className="lab-label lab-label--lg">lg</span>
              </div>
              <div className="component-lab__subsection">
                <strong>{isKorean ? "색상별" : "Colors"}</strong>
              </div>
              <div className="component-lab__demo-row">
                <span className="lab-label lab-label--blue">Blue</span>
                <span className="lab-label lab-label--green">Green</span>
                <span className="lab-label lab-label--rose">Rose</span>
              </div>
              <div className="component-lab__subsection">
                <strong>{isKorean ? "네모 vs 둥근" : "Square vs round"}</strong>
              </div>
              <div className="component-lab__demo-row">
                <span className="lab-label lab-label--square">Square</span>
                <span className="lab-label lab-label--pill">Pill</span>
              </div>
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card"
            title={isKorean ? "Filter" : "Filter"}
            description={
              isKorean
                ? "vetFilter 형태의 삭제 가능한 필터 토큰입니다."
                : "Dismissible filter tokens following the vetFilter pattern."
            }
          >
            <div className="component-lab__demo-row">
              {["sm", "md", "lg"].map((size) => (
                <span className={`vetFilter vetFilter--${size}`} key={size}>
                  <span className="label">Filter {size}</span>
                  <button
                    type="button"
                    className="button image naked"
                    aria-label={isKorean ? "삭제" : "Remove"}
                  >
                    <span className="btnSkin">
                      <span
                        className="icon icon--multiply-outlined"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </span>
              ))}
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card"
            title={isKorean ? "Tabs" : "Tabs"}
            description={
              isKorean
                ? "기본 탭과 라운디드 탭을 한 카드에서 비교합니다."
                : "Basic tabs and rounded tabs in one card."
            }
          >
            <div className="component-lab__demo-stack">
              <div className="component-lab__subsection">
                <strong>{isKorean ? "탭" : "Tabs"}</strong>
              </div>
              <div
                className="lab-tabs"
                role="tablist"
                aria-label={isKorean ? "컴포넌트 탭" : "Component tabs"}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={activeTab === tab.id ? "is-active" : undefined}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="component-lab__subsection">
                <strong>{isKorean ? "라운디드 탭" : "Rounded tabs"}</strong>
              </div>
              <SegmentedControl
                label={isKorean ? "라운디드 탭" : "Rounded tabs"}
                value={activeTab}
                options={roundedTabOptions}
                onValueChange={setActiveTab}
              />
            </div>
            <div className="lab-tab-panel" role="tabpanel">
              {isKorean ? "선택된 탭" : "Selected tab"}:{" "}
              {tabs.find((tab) => tab.id === activeTab)?.label}
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card component-lab__catalog-card--full"
            title={isKorean ? "Date Pickers" : "Date Pickers"}
            description={
              isKorean
                ? "월, 분기, 년도 선택용 picker 패널입니다."
                : "Picker panels for month, quarter, and year selection."
            }
          >
            <div className="calendar-picker-showcase">
              <MonthPicker
                title="MonthPicker"
                value={monthValue}
                locale={dateLocale}
                onChange={setMonthValue}
              />
              <QuarterRangePicker
                title="QuarterRangePicker"
                value={quarterValue}
                locale={dateLocale}
                onChange={setQuarterValue}
              />
              <YearPicker
                title="YearPicker"
                value={yearValue}
                locale={dateLocale}
                onChange={setYearValue}
              />
            </div>
          </Card>

          <Card
            className="component-lab__catalog-card component-lab__catalog-card--wide"
            title={isKorean ? "Calendar" : "Calendar"}
            description={
              isKorean
                ? "특정 날짜 하나를 선택하는 Calendar입니다."
                : "Calendar for selecting a single date."
            }
          >
            <div className="date-calendar-showcase">
              <div className="date-calendar-example">
                <strong>{isKorean ? "캘린더" : "Calendar"}</strong>
                <Calendar
                  value={calendarValue}
                  locale={dateLocale}
                  ariaLabel={
                    isKorean
                      ? "단일 날짜 선택 Calendar"
                      : "Single-date calendar"
                  }
                  onChange={setCalendarValue}
                />
              </div>
              <div className="date-calendar-example">
                <strong>
                  {isKorean ? "취소 확인 캘린더" : "Cancel Confirm Calendar"}
                </strong>
                <Calendar
                  value={actionCalendarValue}
                  locale={dateLocale}
                  ariaLabel={
                    isKorean ? "취소 확인 Calendar" : "Cancel confirm calendar"
                  }
                  showActions
                  onChange={setActionCalendarValue}
                />
              </div>
              <DatePicker
                title={isKorean ? "데이트피커 캘린더" : "DatePicker Calendar"}
                value={datePickerValue}
                locale={dateLocale}
                onChange={setDatePickerValue}
              />
            </div>
          </Card>
          <Card
            className="component-lab__catalog-card component-lab__catalog-card--full"
            title={isKorean ? "Multi Calendar" : "Multi Calendar"}
            description={
              isKorean
                ? "시작 날짜와 종료 날짜를 함께 선택하는 범위 캘린더입니다."
                : "A range calendar for selecting start and end dates together."
            }
          >
            <MultiDatePicker
              title=""
              value={calendarRange}
              locale={dateLocale}
              onChange={setCalendarRange}
            />
          </Card>
          <Card
            className="component-lab__catalog-card"
            title={isKorean ? "Snackbar" : "Snackbar"}
            description={
              isKorean
                ? "하단 중앙 고정 위치에서 일정 시간이 지나면 아래로 사라지는 피드백입니다."
                : "Fixed bottom-center feedback that slides down after a configured duration."
            }
          >
            <div className="lab-snackbar-controls">
              <div className="lab-snackbar-actions">
                {(["success", "error", "info", "warning"] as const).map(
                  (type) => (
                    <button
                      type="button"
                      className="lab-button lab-button--sm"
                      key={type}
                      onClick={() => showSnackbar(type)}
                    >
                      {type}
                    </button>
                  ),
                )}
              </div>
              <label className="lab-snackbar-duration">
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={snackbarDuration}
                  onChange={(event) =>
                    setSnackbarDuration(Number(event.target.value))
                  }
                />
                <span>{isKorean ? "초" : "sec"}</span>
              </label>
            </div>
            <div
              className="lab-snackbar-preview lab-snackbar-preview--success"
              role="status"
            >
              <span className="lab-snackbar__icon" aria-hidden="true">
                {snackbarIcon.success}
              </span>
              <span>{snackbarCopy.success}</span>
            </div>
            {isSnackbarOpen &&
              snackbarRoot &&
              createPortal(
                <div
                  key={snackbarKey}
                  className={`lab-snackbar lab-snackbar--${snackbarType}`}
                  role="status"
                  style={
                    {
                      "--snackbar-duration": `${Math.max(snackbarDuration, 1)}s`,
                    } as CSSProperties
                  }
                >
                  <span className="lab-snackbar__icon" aria-hidden="true">
                    {snackbarIcon[snackbarType]}
                  </span>
                  <span>{snackbarCopy[snackbarType]}</span>
                </div>,
                snackbarRoot,
              )}
          </Card>

          <Card
            className="component-lab__catalog-card"
            title={isKorean ? "Tooltip / Popover" : "Tooltip / Popover"}
            description={
              isKorean
                ? "hover tooltip과 ref 기반 외부 클릭 닫힘 popover입니다."
                : "Hover tooltip and a ref-based outside-click popover."
            }
          >
            <div className="tooltip-popover-lab">
              <span className="lab-tooltip">
                <button type="button" className="lab-button">
                  Tooltip
                </button>
                <span role="tooltip">
                  {isKorean ? "도움말 텍스트" : "Helper tooltip text"}
                </span>
              </span>
              <div className="lab-popover" ref={popoverRef}>
                <button
                  type="button"
                  className={`lab-button ${isPopoverOpen ? "lab-button--blue" : "lab-button--soft"}`}
                  aria-expanded={isPopoverOpen}
                  onClick={() => setIsPopoverOpen((current) => !current)}
                >
                  {isPopoverOpen ? "Popover On" : "Popover Off"}
                </button>
                {isPopoverOpen && (
                  <div className="lab-popover__panel" role="dialog">
                    <strong>
                      {isKorean ? "팝오버 제목" : "Popover title"}
                    </strong>
                    <p>
                      {isKorean
                        ? "외부 영역을 클릭하면 닫힙니다."
                        : "Click outside to close this panel."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </Section>
    </>
  );
};
